import React, { useState, useEffect, useRef } from 'react';
import EmojiPicker from './EmojiPicker';
import LinkPreview from './LinkPreview';
import RichTextEditor from './RichTextEditor';
import { slackEmojiToUnicode } from '../utils/emojiMapper';
import { Node } from 'slate';
import { serializeSlateToMarkdown } from '../utils/slateSerializer';
import './MentionInput.css';

function MentionInput({ value, onChange, onSend, users, channelId, token, onFileSelect, isUploading, hasAttachments = false, enableFormatting = false }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [detectedLinks, setDetectedLinks] = useState([]);
  const [useRichText, setUseRichText] = useState(true);
  const [richTextValue, setRichTextValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ]);
  const inputRef = useRef(null);
  const richTextEditorRef = useRef(null);
  const suggestionsRef = useRef(null);
  const linkDetectionTimeoutRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  // Convert Slate value to plain text
  const serializeSlateValue = (nodes) => {
    return nodes.map(n => Node.string(n)).join('\n');
  };

  useEffect(() => {
    if (mentionSearch && showSuggestions) {
      const searchLower = mentionSearch.toLowerCase();
      const filtered = Object.values(users).filter(user =>
        !user.is_bot &&
        !user.deleted &&
        user.id !== 'USLACKBOT' &&
        (user.name?.toLowerCase().includes(searchLower) ||
         user.real_name?.toLowerCase().includes(searchLower))
      ).slice(0, 8);
      setSuggestions(filtered);
      setSelectedIndex(0);
    } else {
      setSuggestions([]);
    }
  }, [mentionSearch, users, showSuggestions]);

  // Detect links in the message
  useEffect(() => {
    // Clear existing timeout
    if (linkDetectionTimeoutRef.current) {
      clearTimeout(linkDetectionTimeoutRef.current);
    }

    // Debounce link detection
    linkDetectionTimeoutRef.current = setTimeout(() => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const textToCheck = useRichText ? serializeSlateValue(richTextValue) : value;
      const matches = textToCheck.match(urlRegex);

      if (matches) {
        const uniqueLinks = [...new Set(matches)];
        setDetectedLinks(uniqueLinks);
      } else {
        setDetectedLinks([]);
      }
    }, 500);

    return () => {
      if (linkDetectionTimeoutRef.current) {
        clearTimeout(linkDetectionTimeoutRef.current);
      }
    };
  }, [value, richTextValue, useRichText]);

  // Convert emoticons to emojis
  const convertEmoticons = (text) => {
    const emoticons = {
      ':)': '😊',
      ':-)': '😊',
      ':(': '😢',
      ':-(': '😢',
      ':D': '😀',
      ':-D': '😀',
      ';)': '😉',
      ';-)': '😉',
      ':P': '😛',
      ':-P': '😛',
      ':p': '😛',
      ':-p': '😛',
      ':o': '😮',
      ':O': '😮',
      ':-o': '😮',
      ':-O': '😮',
      ':|': '😐',
      ':-|': '😐',
      ':/': '😕',
      ':-/': '😕',
      ':*': '😘',
      ':-*': '😘',
      '<3': '❤️',
      '</3': '💔',
      '^_^': '😊',
      '-_-': '😑',
      'o_O': '😳',
      'O_o': '😳',
      '>_<': '😣',
      'T_T': '😭',
      'XD': '😆',
      'xD': '😆',
    };

    let convertedText = text;

    // Convert emoticons at the end of the text or followed by a space
    Object.keys(emoticons).forEach(emoticon => {
      const escaped = emoticon.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})(\\s|$)`, 'g');
      convertedText = convertedText.replace(regex, (match, p1, p2) => emoticons[emoticon] + p2);
    });

    return convertedText;
  };

  const handleInputChange = (e) => {
    let newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Convert emoticons to emojis
    const originalLength = newValue.length;
    newValue = convertEmoticons(newValue);
    const lengthDiff = newValue.length - originalLength;

    setCursorPosition(cursorPos + lengthDiff);

    // Check if we're typing a mention
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      // Check if there's no space after @ (still typing the mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionSearch(textAfterAt);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }

    onChange(newValue);
  };

  const insertMention = (user) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeMention = value.slice(0, lastAtIndex);
      const afterCursor = value.slice(cursorPosition);
      const mentionText = `@${user.real_name || user.name}`;
      const newValue = beforeMention + mentionText + ' ' + afterCursor;
      
      onChange(newValue);
      setShowSuggestions(false);
      setMentionSearch('');
      
      // Focus back on input and set cursor position
      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = beforeMention.length + mentionText.length + 1;
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev === 0 ? suggestions.length - 1 : prev - 1);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
    // Enter and Shift+Enter create line breaks (default textarea behavior)
  };

  const handleSend = () => {
    let messageText = useRichText ? serializeSlateToMarkdown(richTextValue) : value;

    if (messageText.trim() || hasAttachments) {
      // Extract mentions from the message by matching against known user names
      const mentions = [];
      const usersList = Object.values(users).filter(u => !u.is_bot && !u.deleted);

      // Sort users by name length (longest first) to match longer names first
      const sortedUsers = usersList.sort((a, b) => {
        const nameA = a.real_name || a.name || '';
        const nameB = b.real_name || b.name || '';
        return nameB.length - nameA.length;
      });

      // Find all @mentions by matching against user names
      sortedUsers.forEach(user => {
        const realName = user.real_name;
        const userName = user.name;

        // Check for @real_name mention
        if (realName) {
          const realNamePattern = new RegExp(`@${realName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$|[.,!?])`, 'gi');
          if (realNamePattern.test(messageText)) {
            mentions.push({ username: realName, userId: user.id });
            // Replace with Slack format directly
            messageText = messageText.replace(realNamePattern, `<@${user.id}>`);
          }
        }

        // Check for @username mention
        if (userName && userName !== realName) {
          const userNamePattern = new RegExp(`@${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$|[.,!?])`, 'gi');
          if (userNamePattern.test(messageText)) {
            mentions.push({ username: userName, userId: user.id });
            // Replace with Slack format directly
            messageText = messageText.replace(userNamePattern, `<@${user.id}>`);
          }
        }
      });

      onSend(messageText, mentions);
      setDetectedLinks([]); // Clear link previews after sending

      // Reset rich text editor
      if (useRichText && richTextEditorRef.current) {
        richTextEditorRef.current.clear();
      }
    }
  };

  const handleRichTextChange = (newValue) => {
    setRichTextValue(newValue);
    const plainText = serializeSlateValue(newValue);
    onChange(plainText);
  };

  const handleMentionSearch = (searchText, shouldShow) => {
    setMentionSearch(searchText);
    setShowSuggestions(shouldShow);
  };

  const handleMentionSelect = (action) => {
    if (action === 'down') {
      setSelectedIndex(prev => (prev + 1) % suggestions.length);
    } else if (action === 'up') {
      setSelectedIndex(prev => prev === 0 ? suggestions.length - 1 : prev - 1);
    } else if (action === 'select') {
      const selectedUser = suggestions[selectedIndex];
      if (selectedUser && richTextEditorRef.current) {
        richTextEditorRef.current.insertMention(selectedUser.real_name || selectedUser.name);
        setShowSuggestions(false);
        setMentionSearch('');
      }
    }
  };

  const handlePasteImage = (file) => {
    // Trigger the file select handler with the pasted image
    if (onFileSelect) {
      // Create a synthetic file list
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      // Create a synthetic event
      const syntheticEvent = {
        target: {
          files: dataTransfer.files
        }
      };

      onFileSelect(syntheticEvent);
    }
  };

  const removeLink = (linkToRemove) => {
    setDetectedLinks(prev => prev.filter(link => link !== linkToRemove));
  };

  const handleEmojiSelect = (emojiId) => {
    // Convert emoji ID to Unicode emoji
    const emojiUnicode = slackEmojiToUnicode(emojiId);

    if (useRichText && richTextEditorRef.current) {
      // Insert emoji into Slate editor
      richTextEditorRef.current.insertEmoji(emojiUnicode);
    } else if (inputRef.current) {
      // For plain textarea
      const start = inputRef.current.selectionStart;
      const end = inputRef.current.selectionEnd;
      const newValue = value.substring(0, start) + emojiUnicode + value.substring(end);
      onChange(newValue);

      // Set cursor position after emoji
      setTimeout(() => {
        const newPos = start + emojiUnicode.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }, 0);
    }
    setShowEmojiPicker(false);
  };

  return (
    <div className="mention-input-container">
      <div className="mention-input-wrapper">
        {detectedLinks.length > 0 && (
          <div className="link-previews">
            {detectedLinks.map((link, index) => (
              <LinkPreview
                key={index}
                url={link}
                onRemove={() => removeLink(link)}
              />
            ))}
          </div>
        )}
        {useRichText ? (
          <RichTextEditor
            ref={richTextEditorRef}
            value={richTextValue}
            onChange={handleRichTextChange}
            onSend={handleSend}
            placeholder={enableFormatting ? "Type a message... Use @ to mention, Ctrl+V to paste images. Ctrl+Enter to send." : "Type a message... Use @ to mention. Ctrl+Enter to send."}
            onKeyDown={handleKeyDown}
            convertEmoticons={convertEmoticons}
            enableFormatting={enableFormatting}
            users={users}
            onMentionSearch={handleMentionSearch}
            showMentions={showSuggestions}
            mentions={suggestions}
            selectedMentionIndex={selectedIndex}
            onSelectMention={handleMentionSelect}
            onPasteImage={handlePasteImage}
          />
        ) : (
          <textarea
            ref={inputRef}
            className="mention-input"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (use @ to mention, Ctrl+Enter to send)"
            rows="3"
          />
        )}
        <div className="input-controls">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden-file-input"
            onChange={(e) => onFileSelect && onFileSelect(e)}
            disabled={isUploading}
          />

          <div className="input-controls-left">
            <button
              className="attachment-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              title="Attach files"
              type="button"
            >
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M18 9L10 17C7.8 19.2 4.2 19.2 2 17C-0.2 14.8 -0.2 11.2 2 9L10 1C11.7 -0.7 14.3 -0.7 16 1C17.7 2.7 17.7 5.3 16 7L8 15C6.9 16.1 5.1 16.1 4 15C2.9 13.9 2.9 12.1 4 11L11 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"/>
              </svg>
            </button>

            <div className="emoji-picker-wrapper">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-btn"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
                title="Add emoji"
              >
                😊
              </button>
              <EmojiPicker
                isOpen={showEmojiPicker}
                onClose={() => setShowEmojiPicker(false)}
                onEmojiSelect={handleEmojiSelect}
                referenceElement={emojiButtonRef.current}
              />
            </div>
          </div>

          <button
            className="send-button"
            onClick={handleSend}
            disabled={
              useRichText
                ? !serializeSlateValue(richTextValue).trim() && !hasAttachments
                : !value.trim() && !hasAttachments
            }
          >
            Send
          </button>
        </div>
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="mention-suggestions" ref={suggestionsRef}>
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              className={`mention-suggestion ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => insertMention(user)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-avatar">
                {user.profile?.image_32 ? (
                  <img src={user.profile.image_32} alt={user.real_name} />
                ) : (
                  <div className="avatar-placeholder-small">
                    {(user.real_name || user.name)?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              <div className="suggestion-info">
                <div className="suggestion-name">{user.real_name || user.name}</div>
                {user.real_name && user.name && (
                  <div className="suggestion-username">@{user.name}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MentionInput;